/**
 * Zentraler API-Client. Kapselt fetch() mit Basis-URL, Authorization-Header
 * und einheitlicher Fehlerbehandlung. Bei 401 wird der Benutzer automatisch
 * ausgeloggt (Token abgelaufen/ungültig).
 */

// Exportiert (nicht nur intern genutzt), da einzelne Stellen wie
// LocationsGallery.tsx Bilddateien selbst per fetch() laden müssen (mit
// Authorization-Header, nicht per einfachem <img src>, siehe dort) und
// dafür dieselbe Basis-URL wie der normale API-Client brauchen.
export const API_BASE = import.meta.env.VITE_API_URL ?? "";
const TOKEN_KEY = "lv_token";

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token);
}

export function clearToken(): void {
  localStorage.removeItem(TOKEN_KEY);
}

export class ApiError extends Error {
  status: number;
  details?: unknown;
  constructor(status: number, message: string, details?: unknown) {
    super(message);
    this.status = status;
    this.details = details;
  }
}

let onUnauthorized: (() => void) | null = null;
export function registerUnauthorizedHandler(handler: () => void): void {
  onUnauthorized = handler;
}

interface RequestOptions {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: unknown;
  isFormData?: boolean;
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const headers: Record<string, string> = {};
  const token = getToken();
  if (token) headers["Authorization"] = `Bearer ${token}`;

  let body: BodyInit | undefined;
  if (options.body !== undefined) {
    if (options.isFormData) {
      body = options.body as FormData;
    } else {
      headers["Content-Type"] = "application/json";
      body = JSON.stringify(options.body);
    }
  }

  const res = await fetch(`${API_BASE}${path}`, { method: options.method ?? "GET", headers, body });

  let json: any = null;
  try {
    json = await res.json();
  } catch {
    // kein JSON-Body (z.B. leere Antwort)
  }

  if (!res.ok) {
    if (res.status === 401 && onUnauthorized) onUnauthorized();
    const message = json?.error?.message ?? `Fehler ${res.status}`;
    throw new ApiError(res.status, message, json?.error?.details);
  }

  return (json?.data ?? json) as T;
}

export const api = {
  get: <T>(path: string) => request<T>(path, { method: "GET" }),
  post: <T>(path: string, body?: unknown) => request<T>(path, { method: "POST", body }),
  put: <T>(path: string, body?: unknown) => request<T>(path, { method: "PUT", body }),
  patch: <T>(path: string, body?: unknown) => request<T>(path, { method: "PATCH", body }),
  delete: <T>(path: string) => request<T>(path, { method: "DELETE" }),
  upload: <T>(path: string, formData: FormData) => request<T>(path, { method: "POST", body: formData, isFormData: true }),
};
